import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';

import { FormRadioIndicator } from './FormRadioIndicator';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight } from '../../theme/typography';

export interface FormRadioOptionPressableProps {
  optionLabel: string;
  selected: boolean;
  disabled: boolean;
  onPress: () => void;
  testID?: string;
  /**
   * Use beside an inline legend (e.g. `FormYesNoChoice` `inline`): vertically centers the control
   * + label inside {@link spacing.formRadioOptionMinHeight} so the row aligns with the question
   * text. Default keeps flex-start + {@link spacing.formRadioLabelAlignPadTop} for stacked groups.
   */
  pairWithLegendInline?: boolean;
}

/**
 * One tappable radio row (Figma “Radio Button with label”): 16px control, 8px gap, 14/22 label.
 */
export const FormRadioOptionPressable: React.FC<
  FormRadioOptionPressableProps
> = ({
  optionLabel,
  selected,
  disabled,
  onPress,
  testID,
  pairWithLegendInline = false,
}) => {
  const [focused, setFocused] = React.useState(false);
  const handlePress = () => {
    if (disabled) {
      return;
    }
    onPress();
  };

  return (
    <Pressable
      style={({ pressed }) => [
        styles.optionRow,
        pairWithLegendInline && styles.optionRowInlineLegendPair,
        disabled && styles.optionRowDisabled,
        pressed && !disabled && styles.optionRowPressed,
      ]}
      onPress={handlePress}
      disabled={disabled}
      accessibilityRole="radio"
      accessibilityLabel={optionLabel}
      accessibilityState={{ checked: selected, selected, disabled }}
      testID={testID}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      {...(Platform.OS === 'web' && !disabled ? { tabIndex: 0 as number } : {})}
    >
      <View
        style={[
          styles.radioSlot,
          pairWithLegendInline && styles.radioSlotInlineLegendPair,
        ]}
      >
        <FormRadioIndicator
          selected={selected}
          disabled={disabled}
          focused={focused}
        />
      </View>
      <Text
        style={[styles.optionLabel, disabled && styles.optionLabelDisabled]}
      >
        {optionLabel}
      </Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  optionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    minHeight: spacing.formRadioOptionMinHeight,
    justifyContent: 'flex-start',
    columnGap: spacing.formRadioLabelGap,
    ...Platform.select({
      web: {
        cursor: 'pointer' as const,
        userSelect: 'none' as const,
        outlineStyle: 'none' as const,
      },
      default: {},
    }),
  },
  optionRowDisabled: {
    opacity: 0.55,
  },
  optionRowPressed: {
    opacity: 0.92,
  },
  /** Inline legend row: center disc + label; compact height (stack/row gap handles vertical rhythm). */
  optionRowInlineLegendPair: {
    alignItems: 'center',
    minHeight: lineHeight.formLabel,
  },
  radioSlot: {
    paddingTop:
      spacing.formRadioLabelAlignPadTop - spacing.formRadioFocusBorderWidth,
  },
  radioSlotInlineLegendPair: {
    paddingTop: 0,
  },
  optionLabel: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    color: colors.textPrimary,
  },
  optionLabelDisabled: {
    color: colors.textMuted,
  },
});
